import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { ApolloError, gql } from '@apollo/client';
import {
  ExternalContentFragment,
  GetExternalContentForRestaurantQuery,
  GetExternalContentForRestaurantQueryVariables,
} from '@/types';
import { ExternalContentURLTypes } from '@/enums';
import { getDisplayedNameText } from '@/utils/string';
import { ListOption } from '@/components/list-item';
import { useLayoutContext } from './layout-context';
import { useUserContext } from './user-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

/**
 * Checks if the external content is of type STATIC_URL.
 * @param externalContent - The external content to check.
 * @returns True if the content type is STATIC_URL, otherwise false.
 */
export const isExternalContentStaticLink = (
  externalContent: ExternalContentFragment,
): boolean => {
  return externalContent.config.type === ExternalContentURLTypes.STATIC_URL;
};

export interface ExternalContentContextType {
  errorExternalContent?: ApolloError;
  externalContentLinksList: ExternalContentLinkListItem[];
  fetchExternalContent: () => void;
  loadingExternalContent: boolean;
  selectedExternalContent?: ExternalContentFragment;
  selectedStaticLinkURL?: string;
  setSelectedExternalContent: React.Dispatch<
    React.SetStateAction<ExternalContentFragment | undefined>
  >;
  setSelectedStaticLinkURL: React.Dispatch<
    React.SetStateAction<string | undefined>
  >;
}

export type ExternalContentLinkListItem = ListOption<ExternalContentFragment>;

const GET_EXTERNAL_CONTENT_QUERY = gql`
  fragment ExternalContent on external_content {
    config
    display_name
    name
    uuid
  }

  query GetExternalContentForRestaurant(
    $supplierUUID: uuid!
    $restaurantUUID: uuid!
  ) {
    getExternalContentForRestaurant(
      restaurant_id: $restaurantUUID
      supplier_id: $supplierUUID
    ) {
      external_content {
        ...ExternalContent
      }
    }
  }
`;

export const ExternalContentContext =
  createContext<ExternalContentContextType | null>(null);

export const ExternalContentProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isSmallLayout } = useLayoutContext();
  const {
    selectedRestaurantRelationship: { supplierUUID },
    selectedRestaurantUUID: restaurantUUID,
  } = useUserContext();

  const [fetchEnabled, setFetchEnabled] = useState(!isSmallLayout);
  const [selectedExternalContent, setSelectedExternalContent] =
    useState<ExternalContentFragment>();
  const [selectedStaticLinkURL, setSelectedStaticLinkURL] = useState<string>();

  const {
    data: externalContentData,
    loading: loadingExternalContent,
    error: errorExternalContent,
  } = useWrappedQuery<
    GetExternalContentForRestaurantQuery,
    GetExternalContentForRestaurantQueryVariables
  >(GET_EXTERNAL_CONTENT_QUERY, {
    context: {
      noBatch: true,
    },
    skip: !fetchEnabled,
    variables: {
      restaurantUUID,
      supplierUUID,
    },
  });

  const externalContentLinksList = useMemo(() => {
    return (externalContentData?.getExternalContentForRestaurant || []).reduce<
      ExternalContentLinkListItem[]
    >((acc, { external_content }) => {
      if (external_content) {
        acc.push({
          metadata: external_content,
          title: getDisplayedNameText(
            external_content.display_name,
            external_content.name,
          ),
          value: external_content.uuid,
        });
      }
      return acc;
    }, []);
  }, [externalContentData?.getExternalContentForRestaurant]);

  const fetchExternalContent = useCallback(() => setFetchEnabled(true), []);

  const contextValue = useMemo<ExternalContentContextType>(
    () => ({
      errorExternalContent,
      externalContentLinksList,
      fetchExternalContent,
      loadingExternalContent,
      selectedExternalContent,
      selectedStaticLinkURL,
      setSelectedExternalContent,
      setSelectedStaticLinkURL,
    }),
    [
      errorExternalContent,
      externalContentLinksList,
      fetchExternalContent,
      loadingExternalContent,
      selectedExternalContent,
      selectedStaticLinkURL,
    ],
  );

  return (
    <ExternalContentContext.Provider value={contextValue}>
      {children}
    </ExternalContentContext.Provider>
  );
};

export const useExternalContent = () => {
  const context = useContext(ExternalContentContext);
  if (!context) {
    throw new Error(
      'useExternalContentContext must be used within a ExternalContentProvider',
    );
  }
  return context;
};
